import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import relationships from 'Consts/Relationships';
import { withStyles } from '@material-ui/core/styles';
import {
  fetchGoals,
  saveGoal,
  removeGoal,
  goalResourcesToRecords,
} from 'Helpers/Goals';
import { goalsObjectivesAndInterestsHref } from 'Consts/advisorUris';
import AddMoreInfoButton from 'Components/UI/AddMoreInfoButton';
import DataContainer from 'Components/UI/DataContainer';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import useCollection from 'Hooks/useCollection';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';
import GoalSubsectionFieldlist from 'Components/Universal/Questionnaire/Sections/GoalSubsectionFieldlist';
import { useRefreshGoals } from 'Hooks/useRefreshEvent';

const styles = theme => ({
  title: {
    color: '#1b5a7c',
    fontSize: theme.typography.h4.fontSize,
    fontWeight: theme.typography.h5.fontWeight,
    marginTop: 0,
  },
  goal: {
    display: 'flex',
    marginTop: theme.spacing(1),
  },
  goalNumber: {
    borderRadius: '50%',
    background: theme.palette.facet.accentGreen,
    color: '#ffffff',
    textAlign: 'center',
    flex: '0 0 20px',
    height: '20px',
    lineHeight: '20px',
    fontSize: theme.typography.body1.fontSize,
  },
  goalText: {
    paddingLeft: theme.spacing(1),
    color: theme.palette.facet.primaryGray,
  },
});

const Goals = ({
  className,
  classes,
  hideTitle,
  hideAddGoals,
}) => {
  const location = useLocation();
  const { householdResource, householdId } = useSimpleHousehold();
  const { collection, setCollection: setCollectionData } = useCollection();
  const [resources, setResources] = useState([]);

  function getGoals() {
    if (householdResource && householdResource.relationship(relationships.goals).link('related')) {
      fetchGoals(setCollectionData, householdResource);
    }
  }
  useEffect(() => {
    getGoals();
  }, [householdResource, location.search]);

  useEffect(() => {
    if (collection) {
      setResources(collection.resources().list());
    }
  }, [collection]);

  useRefreshGoals(() => {
    getGoals();
  }, [householdResource]);

  const addGoal = () => {
    const newResource = collection.create();
    newResource.attributes({
      type: collection
        .meta()
        .field('type')
        .export().options[0].id,
      timeHorizonValue: collection
        .meta()
        .field('timeHorizonValue')
        .export().options[0].id,
      timeHorizonType: collection
        .meta()
        .field('timeHorizonType')
        .export().options[0].id,
      priority: collection
        .meta()
        .field('priority')
        .export().options[0].id,
    });

    return newResource.submit()
      .then(getGoals)
      .catch((err) => {
        displayErrorMessage(err, err.errors);
      });
  };
  const hasGoals = () => resources && resources.length > 0;
  const loading = collection === null;

  return (
    <DataContainer
      loading={loading}
    >
      <div className={classNames(classes.root, className)}>
        {!hideTitle && <h4 className={classes.title}>Goals</h4>}
        {hasGoals() || hideAddGoals ? (
          <GoalSubsectionFieldlist
            householdId={Number(householdId)}
            loadedGoals={goalResourcesToRecords(resources)}
            saveGoal={goal => saveGoal(goal, setCollectionData, getGoals)}
            removeGoal={goalID => removeGoal(goalID, resources)}
            planPrompt="View Plan"
            addGoal={() => addGoal()}
            disableAddButton={loading}
          />
        ) : (
          <AddMoreInfoButton
            buttonText="Click here to add goals"
            href={goalsObjectivesAndInterestsHref(householdId)}
          />
        )}
      </div>
    </DataContainer>
  );
};


Goals.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  hideTitle: PropTypes.bool,
  hideAddGoals: PropTypes.bool,
};

Goals.defaultProps = {
  className: '',
  hideTitle: false,
  hideAddGoals: false,
};

export default withStyles(styles)(Goals);
